<template>
    <!-- «Вікторина по меню»: кожен по черзі отримує 3 питання, складені з меню (склад, вага, гострота, ціна).
         Найменше правильних — платить; однаково — платить той, хто думав довше -->
    <div class="bp-wp-game">
        <p class="bp-wp-turn" aria-live="polite">
            <template v-if="state != 'done'">Питання {{ step + 1 }} з {{ PER }} для <b>{{ players[current] }}</b></template>
            <template v-else>Найменше правильних: <b>{{ players[loser] }}</b>!</template>
        </p>
        <div v-if="q && state != 'done'" class="bp-wp-quiz">
            <p class="bp-wp-quiz__q">{{ q.text }}</p>
            <div class="bp-wp-quiz__opts" :class="{ 'is-two': q.options.length == 2 }">
                <button v-for="(opt, i) in q.options" :key="q.id + '-' + i" type="button" class="bp-wp-quiz__opt"
                        :class="{ 'is-right': picked !== null && i === q.answer, 'is-wrong': picked === i && i !== q.answer }"
                        :disabled="picked !== null" @click="answer(i)">{{ opt }}</button>
            </div>
        </div>
        <ol class="bp-wp-board">
            <li v-for="(name, i) in players" :key="i" :class="{ 'is-now': state != 'done' && i == current, 'is-lose': state == 'done' && i == loser }">
                <span class="bp-wp-board__name">{{ name }}</span>
                <span class="bp-wp-board__val">{{ asked[i] ? score[i] + ' з ' + asked[i] : '—' }}</span>
            </li>
        </ol>
    </div>
</template>

<script>
import { INGREDIENT_GROUPS, pizzaTerms } from '@/ingredientGroups'

const PER = 3;
// Інгредієнти, що є майже скрізь, для питань нецікаві
const COMMON = ['сир', 'томатний соус', 'вершки'];
const shuffle = list => list.map(v => [Math.random(), v]).sort((a, b) => a[0] - b[0]).map(p => p[1]);
const pickOne = list => list[Math.floor(Math.random() * list.length)];

export default {
    name: 'GameQuiz',
    props: {
        players: { type: Array, required: true }
    },
    data() {
        return {
            PER,
            current: 0,
            step: 0,
            q: null,
            picked: null,
            shownAt: 0,
            used: {},
            score: this.players.map(() => 0),
            asked: this.players.map(() => 0),
            spent: this.players.map(() => 0),
            state: 'play',
            loser: null,
            seq: 0
        }
    },
    computed: {
        pizzas() {
            const known = [].concat(...INGREDIENT_GROUPS.map(group => group.terms)).filter(term => !COMMON.includes(term));
            return (this.$store.state.products || [])
                .filter(p => p.category_id == 1 && p.is_hidden == 0)
                .map(p => ({ p, title: String(p.title).trim(), terms: pizzaTerms(p).filter(term => known.includes(term)) }))
                .filter(row => row.title);
        }
    },
    watch: {
        // Меню могло ще вантажитись, коли гру відкрили
        pizzas() {
            if (!this.q && this.pizzas.length >= 4) this.next();
        }
    },
    mounted() {
        if (this.pizzas.length >= 4) this.next();
    },
    methods: {
        // Питання одного з п'яти видів; повтори в межах гри відсікаємо за ключем
        make() {
            const rows = this.pizzas;
            const makers = [
                () => {
                    const counts = {};
                    rows.forEach(r => r.terms.forEach(t => { counts[t] = (counts[t] || 0) + 1; }));
                    const rare = Object.keys(counts).filter(t => counts[t] <= 2);
                    if (!rare.length) return null;
                    const term = pickOne(rare), yes = pickOne(rows.filter(r => r.terms.includes(term)));
                    const no = shuffle(rows.filter(r => !r.terms.includes(term))).slice(0, 3);
                    return { key: 'rare-' + term, text: 'У якій піці є ' + term + '?', options: [yes, ...no].map(r => r.title), answer: 0 };
                },
                () => {
                    const row = pickOne(rows.filter(r => r.terms.length));
                    if (!row) return null;
                    const yes = pickOne(row.terms);
                    const pool = [...new Set([].concat(...rows.map(r => r.terms)))].filter(t => !row.terms.includes(t));
                    if (pool.length < 3) return null;
                    return { key: 'in-' + row.title, text: 'Що є в піці «' + row.title + '»?', options: [yes, ...shuffle(pool).slice(0, 3)].map(t => t.charAt(0).toUpperCase() + t.slice(1)), answer: 0 };
                },
                () => {
                    const w = rows.filter(r => Number(r.p.weight_30) > 0);
                    const a = pickOne(w), b = pickOne(w.filter(r => Math.abs(Number(r.p.weight_30) - Number(a.p.weight_30)) >= 40));
                    if (!a || !b) return null;
                    const heavy = Number(a.p.weight_30) > Number(b.p.weight_30) ? a : b, light = heavy === a ? b : a;
                    return { key: 'w-' + [a.title, b.title].sort().join(), text: 'Яка піца 30 см важча?', options: [heavy.title, light.title], answer: 0, more: heavy.title + ' — ' + heavy.p.weight_30 + ' г' };
                },
                () => {
                    const hot = rows.filter(r => Number(r.p.is_spicy) === 1), mild = rows.filter(r => Number(r.p.is_spicy) !== 1);
                    if (!hot.length || mild.length < 3) return null;
                    const yes = pickOne(hot);
                    return { key: 'hot-' + yes.title, text: 'Яка з цих піц гостра?', options: [yes, ...shuffle(mild).slice(0, 3)].map(r => r.title), answer: 0 };
                },
                () => {
                    const row = pickOne(rows.filter(r => Number(r.p.price_30) > 100));
                    if (!row) return null;
                    const price = Number(row.p.price_30);
                    const fakes = shuffle([-40, -20, 20, 40, 60]).slice(0, 3).map(d => price + d);
                    return { key: 'price-' + row.title, text: 'Скільки коштує «' + row.title + '» 30 см?', options: [price, ...fakes].map(v => v + ' грн'), answer: 0 };
                }
            ];
            for (let tries = 0; tries < 30; tries++) {
                const q = pickOne(makers)();
                if (!q || this.used[q.key]) continue;
                this.used[q.key] = true;
                // Перемішуємо варіанти й запам'ятовуємо, де правильний
                const order = shuffle(q.options.map((_, i) => i));
                return Object.assign(q, { options: order.map(i => q.options[i]), answer: order.indexOf(q.answer), id: ++this.seq });
            }
            return null;
        },
        next() {
            this.q = this.make();
            this.picked = null;
            this.shownAt = performance.now();
        },
        answer(i) {
            if (this.picked !== null || !this.q) return;
            this.picked = i;
            const who = this.current;
            this.$set(this.spent, who, this.spent[who] + (performance.now() - this.shownAt));
            this.$set(this.asked, who, this.asked[who] + 1);
            if (i === this.q.answer) this.$set(this.score, who, this.score[who] + 1);
            setTimeout(() => {
                if (this.step < PER - 1) { this.step++; this.next(); return; }
                if (this.current < this.players.length - 1) { this.current++; this.step = 0; this.next(); return; }
                // Найменше правильних; нічия — довше думав
                const min = Math.min(...this.score);
                const low = this.players.map((_, k) => k).filter(k => this.score[k] === min);
                this.loser = low.reduce((a, b) => (this.spent[b] > this.spent[a] ? b : a));
                this.state = 'done';
                const sec = Math.round(this.spent[this.loser] / 100) / 10;
                setTimeout(() => this.$emit('done', { loser: this.loser, note: min + ' з ' + PER + ' правильних' + (low.length > 1 ? ', на роздуми ' + sec + ' с' : '') }), 1300);
            }, 1100);
        }
    }
}
</script>
